import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { TrophyIcon, ArrowPathIcon, PlusCircleIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import duelService from '../services/duelService';
import { toast } from 'react-hot-toast';
import websocketRoomClient from '../utils/websocketRoomClient';

// Import the new components
import CreateRoomModal from '../components/duel/CreateRoomModal';
import RoomCard from '../components/duel/RoomCard';
import RoomDetails from '../components/duel/RoomDetails';

const Duel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { roomId } = useParams();

  const [activeRooms, setActiveRooms] = useState([]);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [stats, setStats] = useState({
    rating: 1000,
    wins: 0,
    losses: 0,
    rank: '--'
  });

  const [leaderboard, setLeaderboard] = useState([]);

  // Function to refresh current room data
  const refreshCurrentRoom = useCallback(async (targetRoomId, shouldNavigate = false) => {
    if (!targetRoomId) return;

    // Skip refresh if the requested room is different from current route
    // unless shouldNavigate is true
    if (roomId && targetRoomId !== roomId && !shouldNavigate) {
      console.log('Skipping refresh for different room:', targetRoomId, 'current:', roomId);
      return;
    }

    try {
      // Don't set loading state to avoid UI flicker during background refresh
      const response = await duelService.getRoomDetails(targetRoomId);

      if (response?.success) {
        setCurrentRoom(prev => {
          // If we don't have a current room, always update
          if (!prev) return response.data;

          // If this is the current room and there are changes
          if (prev.roomId === targetRoomId) {
            const newData = response.data;

            // Check for meaningful differences to avoid unnecessary updates
            const hasNewChat = !prev.chat ||
                            (newData.chat && prev.chat.length !== newData.chat.length);
            const hasParticipantChanges = !prev.participants ||
                            newData.participants.length !== prev.participants.length ||
                            JSON.stringify(prev.participants) !== JSON.stringify(newData.participants);
            const hasStatusChanges = prev.status !== newData.status;

            if (hasNewChat || hasParticipantChanges || hasStatusChanges) {
              // Merge the existing chat with new chat messages
              // This prevents losing local messages that might not have reached the server yet
              const mergedChat = [...(prev.chat || [])];
              if (newData.chat && newData.chat.length > 0) {
                newData.chat.forEach(newMsg => {
                  const exists = mergedChat.some(
                    oldMsg => oldMsg.userId === newMsg.userId &&
                              oldMsg.timestamp === newMsg.timestamp &&
                              oldMsg.message === newMsg.message
                  );
                  if (!exists) {
                    mergedChat.push(newMsg);
                  }
                });
              }

              // Sort chat by timestamp
              mergedChat.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

              // Return the merged room data
              return {
                ...newData,
                chat: mergedChat
              };
            }
          }
          return prev;
        });
      }
    } catch (error) {
      console.warn('Error refreshing room details:', error);
    }
  }, [roomId]);

  // Fetch active rooms at regular intervals
  // Initialize WebSocket connection
  useEffect(() => {
    if (!user?.address) return;

    setIsLoading(true);

    // Setup polling interval reference so it can be cleaned up
    let pollIntervalId = null;

    // Function to start a polling fallback for real-time updates
    const startPolling = () => {
      console.log('Starting fallback polling for real-time updates');
      // Clear any existing interval first
      if (pollIntervalId) clearInterval(pollIntervalId);

      // Start a new polling interval - fetch rooms less frequently to avoid excessive requests
      pollIntervalId = setInterval(() => {
        fetchRooms(false);

        // Also fetch current room details if we're viewing a room
        if (roomId && currentRoom) {
          // Pass current roomId directly to avoid any routing issues
          refreshCurrentRoom(roomId, false);
        }
      }, 10000); // Increased to 10 seconds to reduce server load

      return pollIntervalId;
    };

    const connectWebSocket = async () => {
      try {
        // Connect to WebSocket server
        await websocketRoomClient.connect(user.address);

        // Setup event handlers
        websocketRoomClient.on('roomsUpdate', handleRoomsUpdate);
        websocketRoomClient.on('roomCreated', handleRoomCreated);
        websocketRoomClient.on('roomUpdated', handleRoomUpdated);
        websocketRoomClient.on('roomClosed', handleRoomClosed);
        websocketRoomClient.on('chatMessage', handleChatMessage);
        websocketRoomClient.on('gameLaunch', handleGameLaunch);
        websocketRoomClient.on('gameStartBroadcast', handleGameStartBroadcast);
        websocketRoomClient.on('error', handleWebSocketError);

        // Initial fetch for fallback
        try {
          const roomsResponse = await duelService.getActiveRooms();
          if (roomsResponse?.success && Array.isArray(roomsResponse.data)) {
            setActiveRooms(roomsResponse.data);
          }
        } catch (error) {
          console.warn('Could not fetch initial rooms from API, waiting for WebSocket data');
        }

        // Even with websocket connection, start a less frequent polling fallback
        // for redundancy - this ensures we still get updates if websocket messages are missed
        const intervalId = startPolling();

        setIsLoading(false);

        return () => {
          if (intervalId) clearInterval(intervalId);
        };
      } catch (error) {
        console.error('Failed to connect to WebSocket server:', error);
        toast.error('Failed to connect to game server. Falling back to API polling.');

        // Fallback to more frequent polling if websocket fails
        fetchRooms(true);
        const intervalId = startPolling();
        setIsLoading(false);

        return () => {
          if (intervalId) clearInterval(intervalId);
        };
      }
    };

    const cleanup = connectWebSocket();

    // Clean up WebSocket connection, event listeners, and polling intervals
    return () => {
      if (cleanup && typeof cleanup === 'function') cleanup();
      if (pollIntervalId) clearInterval(pollIntervalId);

      websocketRoomClient.off('roomsUpdate', handleRoomsUpdate);
      websocketRoomClient.off('roomCreated', handleRoomCreated);
      websocketRoomClient.off('roomUpdated', handleRoomUpdated);
      websocketRoomClient.off('roomClosed', handleRoomClosed);
      websocketRoomClient.off('chatMessage', handleChatMessage);
      websocketRoomClient.off('gameLaunch', handleGameLaunch);
      websocketRoomClient.off('gameStartBroadcast', handleGameStartBroadcast);
      websocketRoomClient.off('error', handleWebSocketError);
    };
  }, [user?.address, roomId, currentRoom, refreshCurrentRoom]);

  // WebSocket event handlers
  const handleRoomsUpdate = useCallback((rooms) => {
    console.log('Rooms update from WebSocket:', rooms);
    if (Array.isArray(rooms)) {
      setActiveRooms(rooms);

      // If we're viewing a specific room, find and update it
      if (roomId) {
        const updatedRoom = rooms.find(r => r.roomId === roomId);
        if (updatedRoom) {
          setCurrentRoom(prevRoom => {
            // Only update if there are real changes (to avoid flickering)
            if (!prevRoom) return updatedRoom;

            // Check for meaningful differences
            const hasNewChat = !prevRoom.chat ||
                            (updatedRoom.chat && prevRoom.chat.length !== updatedRoom.chat.length);
            const hasParticipantChanges = !prevRoom.participants ||
                            updatedRoom.participants.length !== prevRoom.participants.length;
            const hasStatusChanges = prevRoom.status !== updatedRoom.status;

            if (hasNewChat || hasParticipantChanges || hasStatusChanges) {
              return updatedRoom;
            }
            return prevRoom;
          });
        }
      }

      setIsLoading(false);
    }
  }, [roomId]);

  const handleRoomCreated = useCallback((room) => {
    console.log('Room created:', room);
    setActiveRooms(prev => {
      // Avoid duplicates
      const filtered = prev.filter(r => r.roomId !== room.roomId);
      return [...filtered, room];
    });

    // If we're in the current room, update it
    if (roomId && room.roomId === roomId) {
      setCurrentRoom(room);
    }
  }, [roomId]);

  const handleRoomUpdated = useCallback((room) => {
    console.log('Room updated:', room);
    setActiveRooms(prev => {
      // Replace the room in the list
      return prev.map(r => r.roomId === room.roomId ? room : r);
    });

    // If we're in the current room, update it
    if (roomId && room.roomId === roomId) {
      setCurrentRoom(prev => {
        if (!prev) return room;

        // Merge the existing chat with new chat messages if any
        // This ensures we don't lose locally added messages that might not be in the update yet
        const mergedChat = [...(prev.chat || [])];
        if (room.chat && room.chat.length > 0) {
          // Add only new messages from the update
          room.chat.forEach(newMsg => {
            const exists = mergedChat.some(
              oldMsg => oldMsg.userId === newMsg.userId &&
                        oldMsg.timestamp === newMsg.timestamp &&
                        oldMsg.message === newMsg.message
            );
            if (!exists) {
              mergedChat.push(newMsg);
            }
          });
        }

        // Return the merged room data
        return {
          ...room,
          chat: mergedChat
        };
      });
    }
  }, [roomId]);

  const handleRoomClosed = useCallback((data) => {
    console.log('Room closed:', data.roomId);
    setActiveRooms(prev => prev.filter(r => r.roomId !== data.roomId));

    // If we're in the current room and it was closed, redirect back
    if (currentRoom && currentRoom.roomId === data.roomId) {
      toast.info('This room has been closed');
      navigate('/duel');
    }
  }, [currentRoom, navigate]);

  const handleChatMessage = useCallback((data) => {
    console.log('Chat message:', data);
    // If we're in the current room, update the chat
    if (roomId && data.roomId === roomId) {
      setCurrentRoom(prev => {
        if (!prev) return prev;

        // Check if this message is already in the chat (avoid duplicates)
        const messageExists = (prev.chat || []).some(
          msg => msg.userId === data.message.userId &&
                msg.timestamp === data.message.timestamp &&
                msg.message === data.message.message
        );

        if (messageExists) {
          return prev;
        }

        // Add new message to chat
        return {
          ...prev,
          chat: [...(prev.chat || []), data.message]
        };
      });

      // Force a refresh of room details to get any other updates
      setTimeout(() => refreshCurrentRoom(roomId, false), 500);
    }
  }, [roomId, refreshCurrentRoom]);

  const handleGameLaunch = useCallback((data) => {
    console.log('Game launch received:', data);

    if (data && data.url) {
      toast.success('Game is ready! Redirecting to game...', {
        duration: 3000
      });

      // Short delay to allow toast to be seen before redirect
      setTimeout(() => {
        window.location.href = data.url;
      }, 1000);
    } else {
      console.error('Game launch event missing URL data');
      toast.error('Error launching game: Missing game URL');
    }
  }, []);

  const handleGameStartBroadcast = useCallback((data) => {
    console.log('Game start broadcast received:', data);

    if (!data || !user?.address) return;

    const { challenger, rival, roomId, matchId } = data;

    if (!challenger || !rival) {
      console.error('Game start broadcast missing challenger or rival data');
      return;
    }

    // Normalize user IDs for case-insensitive comparison
    const normalizedUserId = user.address.toLowerCase();
    const normalizedChallengerId = challenger.userId?.toLowerCase();
    const normalizedRivalId = rival.userId?.toLowerCase();

    console.log(`Comparing user IDs: current=${normalizedUserId}, challenger=${normalizedChallengerId}, rival=${normalizedRivalId}`);

    if (normalizedChallengerId === normalizedUserId && challenger.url) {
      console.log('Match found: User is the challenger');
      toast.success('Game is starting! You are the challenger.', {
        duration: 3000
      });

      // Update UI to show game is starting
      if (currentRoom && currentRoom.roomId === roomId) {
        setCurrentRoom(prev => ({
          ...prev,
          status: 'in_game'
        }));
      }

      setTimeout(() => {
        console.log('Redirecting challenger to game:', challenger.url);
        window.location.href = challenger.url;
      }, 1000);
    } else if (normalizedRivalId === normalizedUserId && rival.url) {
      console.log('Match found: User is the rival');
      toast.success('Game is starting! You are the rival.', {
        duration: 3000
      });

      // Update UI to show game is starting
      if (currentRoom && currentRoom.roomId === roomId) {
        setCurrentRoom(prev => ({
          ...prev,
          status: 'in_game'
        }));
      }

      setTimeout(() => {
        console.log('Redirecting rival to game:', rival.url);
        window.location.href = rival.url;
      }, 1000);
    } else {
      console.log('Game start broadcast not for this user');
    }
  }, [user?.address, currentRoom]);

  const handleWebSocketError = useCallback((error) => {
    console.error('WebSocket error:', error);
    toast.error(error.message || 'Error connecting to game server', {
      duration: 5000,
      position: 'top-center',
      style: {
        maxWidth: '500px',
        wordBreak: 'break-word'
      }
    });
  }, []);

  // Fallback polling function
  const fetchRooms = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) setIsLoading(true);

      // Get active rooms from service
      const roomsResponse = await duelService.getActiveRooms();

      if (roomsResponse?.success && Array.isArray(roomsResponse.data)) {
        setActiveRooms(roomsResponse.data);

        // If we're viewing a specific room, check if it's in the updated list
        if (roomId) {
          const updatedRoom = roomsResponse.data.find(r => r.roomId === roomId);
          if (updatedRoom) {
            refreshCurrentRoom(roomId, false);
          }
        }
      } else {
        // Last resort: Try to get rooms directly from localStorage
        try {
          const localStorageHelper = await import('../utils/localStorageHelper');
          const localRooms = localStorageHelper.getAllRoomsFromLocalStorage().filter(
            room => room.status === 'active'
          );

          if (localRooms.length > 0) {
            setActiveRooms(localRooms);
          } else {
            setActiveRooms([]);
          }
        } catch (error) {
          setActiveRooms([]);
        }
      }
    } catch (error) {
      console.error('Error fetching rooms:', error);
      setActiveRooms([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch room details if roomId is provided and not already set by WebSocket
  useEffect(() => {
    if (!roomId) {
      setCurrentRoom(null);
      return;
    }

    // Check if we already have the room from WebSocket
    const existingRoom = activeRooms.find(r => r.roomId === roomId);
    if (existingRoom) {
      setCurrentRoom(existingRoom);
      return;
    }

    // Keep track of whether this effect is still current to avoid race conditions
    let isCurrentEffect = true;

    const fetchRoomDetails = async () => {
      try {
        setIsLoading(true);

        // Try API first
        try {
          const response = await duelService.getRoomDetails(roomId);

          // Only update if this effect is still current
          if (isCurrentEffect) {
            if (response?.success) {
              setCurrentRoom(response.data);
            } else {
              throw new Error('Room not found in API response');
            }
          }
        } catch (apiError) {
          console.warn('Error with API call for room details:', apiError);

          if (!isCurrentEffect) return;

          // Try localStorage as fallback
          const localStorageHelper = await import('../utils/localStorageHelper');
          const localRoom = localStorageHelper.getRoomFromLocalStorage(roomId);

          if (localRoom) {
            console.log('Found room in localStorage:', roomId);
            setCurrentRoom(localRoom);
          } else {
            setError('Room not found');
            setCurrentRoom(null);
            navigate('/duel');
            toast.error('Room not found');
          }
        }
      } catch (error) {
        if (!isCurrentEffect) return;

        console.error('Error fetching room details:', error);
        setError('Failed to load room');
        setCurrentRoom(null);
        navigate('/duel');
        toast.error('Failed to load room');
      } finally {
        if (isCurrentEffect) {
          setIsLoading(false);
        }
      }
    };

    fetchRoomDetails();

    // Cleanup function - mark this effect as no longer current if roomId changes
    return () => {
      isCurrentEffect = false;
    };
  }, [roomId, navigate]);

  // Fetch leaderboard and player stats when user mounts or changes
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch leaderboard
        try {
          const leaderboardResponse = await duelService.getLeaderboard();
          if (leaderboardResponse?.success) {
            setLeaderboard(leaderboardResponse.data);
          }
        } catch (leaderboardError) {
          console.error('Error fetching leaderboard:', leaderboardError);
        }

        // If user is logged in, fetch their stats
        if (user?.address) {
          try {
            const statsResponse = await duelService.getPlayerStats(user.address);
            if (statsResponse?.success) {
              setStats(statsResponse.data);
            }
          } catch (statsError) {
            console.error('Error fetching user stats:', statsError);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [user?.address]);

  const handleCreateRoom = async (formData) => {
    try {
      if (!user?.address) {
        toast.error('You must be logged in to create a room');
        return;
      }

      setIsLoading(true);

      // Generate a unique room ID
      const roomId = `duel_${Date.now()}_${Math.floor(Math.random() * 10000)}`;

      const roomData = {
        roomId,
        name: formData.roomName,
        createdBy: {
          userId: user.address,
          username: user.username || user.address.substring(0, 6) + '...'
        },
        maxParticipants: formData.maxParticipants
      };

      // Optimistically add room to UI
      const optimisticRoom = {
        ...roomData,
        participants: [
          {
            userId: user.address,
            username: user.username || user.address.substring(0, 6) + '...',
            joinedAt: new Date().toISOString(),
            isReady: false
          }
        ],
        status: 'active',
        matches: [],
        chat: []
      };

      setActiveRooms(prev => [...prev, optimisticRoom]);

      // Create room via WebSocket if connected
      if (websocketRoomClient.connected) {
        websocketRoomClient.createRoom(roomData);
      } else {
        // Fallback to API
        const response = await duelService.createRoom({
          userId: user.address,
          username: user.username || user.address.substring(0, 6) + '...',
          roomName: formData.roomName,
          maxParticipants: formData.maxParticipants
        });

        if (!response?.success) {
          toast.error('Failed to create room via API');
        }
      }

      toast.success('Room created successfully');

      // Navigate to the room details page
      navigate(`/duel/${roomId}`);
    } catch (error) {
      console.error('Error creating room:', error);
      toast.error('Error creating room');
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoinRoom = async (roomId) => {
    try {
      if (!user?.address) {
        toast.error('You must be logged in to join a room');
        return;
      }

      setIsLoading(true);

      const username = user.username || user.address.substring(0, 6) + '...';

      // Join room via WebSocket if connected
      if (websocketRoomClient.connected) {
        websocketRoomClient.joinRoom(roomId, user.address, username);
      } else {
        // Fallback to API
        const response = await duelService.joinRoom(roomId, {
          userId: user.address,
          username
        });

        if (!response?.success) {
          toast.error('Failed to join room via API');
          setIsLoading(false);
          return;
        }
      }

      toast.success('Joining room...');

      // Navigate to the room details page
      navigate(`/duel/${roomId}`);
    } catch (error) {
      console.error('Error joining room:', error);
      toast.error('Error joining room');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLeaveRoom = async (roomId) => {
    try {
      if (!user?.address) {
        toast.error('You must be logged in to leave a room');
        return;
      }

      setIsLoading(true);

      // Leave room via WebSocket if connected
      if (websocketRoomClient.connected) {
        websocketRoomClient.leaveRoom(roomId, user.address);
      } else {
        // Fallback to API
        const response = await duelService.leaveRoom(roomId, {
          userId: user.address
        });

        if (!response?.success) {
          toast.error('Failed to leave room via API');
          setIsLoading(false);
          return;
        }
      }

      toast.success('Left room successfully');

      // Navigate back to the rooms list
      navigate('/duel');
    } catch (error) {
      console.error('Error leaving room:', error);
      toast.error('Error leaving room');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (messageData) => {
    try {
      // Create timestamp early so we can use it for optimistic update
      const timestamp = new Date().toISOString();

      // Optimistically update current room chat
      if (currentRoom) {
        const newMessage = {
          userId: messageData.userId,
          username: messageData.username,
          message: messageData.message,
          timestamp: timestamp
        };

        setCurrentRoom(prev => ({
          ...prev,
          chat: [...(prev.chat || []), newMessage]
        }));
      }

      // Send message via WebSocket if connected
      if (websocketRoomClient.connected) {
        websocketRoomClient.sendChatMessage(
          messageData.roomId,
          messageData.userId,
          messageData.username,
          messageData.message
        );
      } else {
        // Fallback to API
        const response = await duelService.addChatMessage(
          messageData.roomId,
          {
            userId: messageData.userId,
            username: messageData.username,
            message: messageData.message
          }
        );

        if (!response?.success) {
          toast.error('Failed to send message via API');
        } else {
          // Force room refresh to get any updated chat messages
          setTimeout(() => refreshCurrentRoom(messageData.roomId, false), 500);
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Error sending message');
    }
  };

  const handleSetReady = async (roomId, isReady) => {
    try {
      if (!user?.address) {
        toast.error('You must be logged in to set ready status');
        return;
      }

      // Optimistically update current room participants
      if (currentRoom) {
        setCurrentRoom(prev => {
          return {
            ...prev,
            participants: prev.participants.map(p =>
              p.userId === user.address ? { ...p, isReady } : p
            )
          };
        });
      }

      // Set ready status via WebSocket if connected
      if (websocketRoomClient.connected) {
        websocketRoomClient.setReadyStatus(roomId, user.address, isReady);

        // Force a refresh after a short delay to get server confirmation
        setTimeout(() => refreshCurrentRoom(roomId, false), 500);
      } else {
        // Fallback to API
        const response = await duelService.setParticipantReady(roomId, {
          userId: user.address,
          isReady
        });

        if (!response?.success) {
          toast.error('Failed to update ready status via API');

          // Revert optimistic update if API fails
          if (currentRoom) {
            setCurrentRoom(prev => {
              return {
                ...prev,
                participants: prev.participants.map(p =>
                  p.userId === user.address ? { ...p, isReady: !isReady } : p
                )
              };
            });
          }
        } else {
          // If API succeeded, refresh the room data
          refreshCurrentRoom(roomId, false);
        }
      }
    } catch (error) {
      console.error('Error setting ready status:', error);
      toast.error('Error setting ready status');
    }
  };

const handlePlay = async (roomId) => {
  try {
    if (!user?.address) {
      toast.error('You must be logged in to start the game');
      return;
    }

    console.log('Play button clicked with user:', user.address);
    console.log('Current room from state:', currentRoom);
    console.log('Looking for room in activeRooms...', activeRooms);

    // First check currentRoom since we're viewing this room
    let room = currentRoom;

    // If we don't have currentRoom or it doesn't match roomId, try to find it in activeRooms
    if (!room || room.roomId !== roomId) {
      console.log('Room not found in currentRoom, searching activeRooms');
      room = activeRooms.find(r => r.roomId === roomId);
    }

    // If still not found, fetch directly from the server or localStorage
    if (!room) {
      console.log('Room not found in activeRooms, fetching from server/localStorage');
      try {
        const response = await duelService.getRoomDetails(roomId);
        if (response?.success) {
          room = response.data;
          console.log('Retrieved room from server:', room);
        }
      } catch (err) {
        console.error('Failed to fetch room:', err);
        toast.error('Failed to fetch room details. Please try again.');
        return;
      }
    }

    console.log('Final room object for validation:', room);
    console.log('Room creator:', room?.createdBy?.userId);

    // Normalize user IDs for case-insensitive comparison
    const normalizedUserID = user.address.toLowerCase();
    const normalizedCreatorID = room?.createdBy?.userId?.toLowerCase();

    console.log('Normalized IDs - User:', normalizedUserID, 'Creator:', normalizedCreatorID);

    // Compare normalized IDs
    if (!room) {
      console.error('Room not found for validation');
      toast.error('Room not found. Please refresh the page and try again.');
      return;
    }

    if (normalizedCreatorID !== normalizedUserID) {
      console.error('Creator validation failed:', {
        roomExists: true,
        userID: user.address,
        creatorID: room.createdBy?.userId
      });
      toast.error('Only the room creator can start the game');
      return;
    }

    // Count ready players
    const readyPlayersCount = room.participants.filter(p => p.isReady).length;
    if (readyPlayersCount < 2) {
      toast.error('Need at least 2 ready players to start');
      return;
    }

    // Check if the room is already in game
    if (room.status === 'in_game') {
      console.log('Room already in game state, cannot start again');
      toast.error('Game is already in progress. Please wait for it to finish or restart the room.');
      return;
    }

    // Ensure WebSocket connection before attempting to start the game
    if (!websocketRoomClient.connected) {
      console.log('WebSocket not connected, attempting to reconnect');
      try {
        await websocketRoomClient.connect(user.address);
        console.log('WebSocket reconnected successfully');
      } catch (err) {
        console.error('Failed to reconnect WebSocket:', err);
        toast.error('Failed to connect to game server. Please try again later.');
        return;
      }
    }

    toast.success('Matching ready players...');

    // Force a refresh of the room state first to ensure we have the latest data
    await refreshCurrentRoom(roomId, false);

    // Update room status to 'in_game' immediately for better UX
    setCurrentRoom(prev => ({
      ...prev,
      status: 'in_game'
    }));

    // Start game via WebSocket with proper promise handling
    try {
      const response = await websocketRoomClient.startGame(roomId, user.address);
      console.log('Game start response:', response);

      if (response.success) {
        toast.success('Game starting! Redirecting...', {
          icon: '🎮',
          duration: 3000
        });

        // If we didn't get a redirect from the WS server directly,
        // construct game URL ourselves as a fallback
        if (!response.data?.url) {
          console.log('No game URL in response, constructing a fallback URL');
          // Wait brief moment before trying to navigate manually
          setTimeout(async () => { // Make the callback async
            try {
              // Fetch the latest room details to ensure we have the matchId
              let finalRoomData = room; // Start with the current room data
              try {
                console.log(`Fetching match ID for room ${roomId} via API endpoint`);

                // Call the new API endpoint we created to get the match ID by room ID
                try {
                  const response = await fetch(`/api/get-match-by-room?room_id=${roomId}`);
                  const matchData = await response.json();

                  if (matchData.status === 1 && matchData.data.match_id) {
                    console.log(`API returned match ID: ${matchData.data.match_id} for room ${roomId}`);
                    // Update the room data with the correct match ID
                    finalRoomData = {
                      ...finalRoomData,
                      matchId: matchData.data.match_id
                    };
                  } else {
                    console.warn(`API did not return a valid match ID: ${JSON.stringify(matchData)}`);

                    // Try fetching full room details as fallback
                    const freshRoomResponse = await duelService.getRoomDetails(roomId);
                    if (freshRoomResponse?.success) {
                      finalRoomData = freshRoomResponse.data;
                      console.log('Successfully fetched fresh room data:', finalRoomData);
                    } else {
                      console.warn('Could not fetch fresh room details for fallback URL, using potentially stale data.');
                    }
                  }
                } catch (apiError) {
                  console.error('Error calling get-match-by-room API:', apiError);

                  // Try fetching full room details as fallback
                  const freshRoomResponse = await duelService.getRoomDetails(roomId);
                  if (freshRoomResponse?.success) {
                    finalRoomData = freshRoomResponse.data;
                    console.log('Successfully fetched fresh room data:', finalRoomData);
                  } else {
                    console.warn('Could not fetch fresh room details for fallback URL, using potentially stale data.');
                  }
                }
              } catch (fetchErr) {
                console.error('Error fetching fresh room details for fallback URL:', fetchErr);
              }

              // This logic should only run if the websocket doesn't automatically redirect
              const protocol = window.location.protocol;
              const hostname = window.location.hostname;
              const port = '3001'; // Hardcoded game port

              // Get participant details from the potentially updated room state
              const challenger = finalRoomData?.participants?.find(p => p.userId === finalRoomData.createdBy.userId);
              const rival = finalRoomData?.participants?.find(p => p.userId !== finalRoomData.createdBy.userId && p.isReady); // Find the first ready rival

              if (!challenger || !rival) {
                console.error('Cannot construct fallback URL: Missing challenger or rival details');
                toast.error('Failed to construct game URL. Please try again.');
                return;
              }

              // Determine user's role
              const userRole = user.address.toLowerCase() === challenger.userId.toLowerCase() ? 'challenger' : 'rival';

              // Construct the full URL with all necessary parameters, using the potentially updated finalRoomData
              const poolId = finalRoomData.matchId || 'unknown'; // Use fetched matchId
              if (poolId === 'unknown') {
                 console.warn(`Fallback URL constructed with poolID='unknown'. Match ID might not be available yet.`);
              }
              const gameUrl = `${protocol}//${hostname}:${port}/?roomID=${roomId}&poolID=${poolId}&ts=${Date.now()}&userID=${user.address}&challenger=${challenger.userId}&rival=${rival.userId}&role=${userRole}`;

              console.log('Manually navigating to game URL (Fallback):', gameUrl);
              window.location.href = gameUrl;
            } catch (err) {
              console.error('Failed to manually navigate to game:', err);
            }
          }, 2000);
        }
      } else {
        console.error('Game start successful but missing data:', response);
        // Keep the in_game status and hope for the best
      }
    } catch (error) {
      console.error('Error starting game:', error);
      toast.error(error.message || 'Failed to start game. Please try again.');

      // Revert room status update if there was an error
      setCurrentRoom(prev => ({
        ...prev,
        status: 'active'
      }));
    }
  } catch (error) {
    console.error('Error in handlePlay:', error);
    toast.error('An unexpected error occurred. Please try again.');
  }
};

  const refreshData = async () => {
    setIsLoading(true);

    try {
      // Refresh via WebSocket if connected
      if (websocketRoomClient.connected) {
        websocketRoomClient.sendMessage({ type: 'GET_ROOMS' });
      }

      // Also refresh leaderboard and stats through API (these don't come through WebSocket)
      const leaderboardResponse = await duelService.getLeaderboard();
      if (leaderboardResponse?.success) {
        setLeaderboard(leaderboardResponse.data);
      }

      if (user?.address) {
        const statsResponse = await duelService.getPlayerStats(user.address);
        if (statsResponse?.success) {
          setStats(statsResponse.data);
        }
      }

      // Refresh current room if viewing one
      if (roomId) {
        refreshCurrentRoom(roomId, false);
      }

      // As a fallback, also refresh rooms through API
      const roomsResponse = await duelService.getActiveRooms();
      if (roomsResponse?.success) {
        setActiveRooms(prevRooms => {
          // If we already have rooms from WebSocket, don't overwrite
          // Instead, look for any rooms from API that we don't have yet
          if (prevRooms.length > 0) {
            const existingRoomIds = new Set(prevRooms.map(r => r.roomId));
            const newRooms = roomsResponse.data.filter(r => !existingRoomIds.has(r.roomId));
            return [...prevRooms, ...newRooms];
          }
          return roomsResponse.data;
        });
      }

      toast.success('Data refreshed');
    } catch (error) {
      console.error('Error refreshing data:', error);
      toast.error('Error refreshing data');
    } finally {
      setIsLoading(false);
    }
  };

  // Render room details if a room is selected
  if (roomId && currentRoom) {
    // If no user is connected, show view-only mode with a connect wallet prompt
    if (!user?.address) {
      return (
        <div className="container mx-auto px-4 py-8">
          <div className="bg-yellow-600/20 border border-yellow-600/50 rounded-lg p-4 mb-6">
            <p className="text-yellow-300 text-center">
              Connect your wallet to join this room and play. View-only mode enabled.
            </p>
          </div>
          <RoomDetails
            room={currentRoom}
            userId={null}
            username={'Guest'}
            onLeaveRoom={() => navigate('/duel')}
            onSendMessage={() => toast.error('Please connect your wallet to chat')}
            onSetReady={() => toast.error('Please connect your wallet to set ready status')}
            onPlay={() => toast.error('Please connect your wallet to play')}
            viewOnly={true}
          />
        </div>
      );
    }

    return (
      <div className="container mx-auto px-4 py-8">
        <RoomDetails
          room={currentRoom}
          userId={user?.address}
          username={user?.username || user?.address?.substring(0, 6) + '...'}
          onLeaveRoom={handleLeaveRoom}
          onSendMessage={handleSendMessage}
          onSetReady={handleSetReady}
          onPlay={handlePlay}
          viewOnly={false}
        />
      </div>
    );
  }

  // Render room list (main screen)
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white">Pool Duel Mode</h1>
        <div className="flex gap-3">
          <button
            onClick={refreshData}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg"
            disabled={isLoading}
          >
            <ArrowPathIcon className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg"
          >
            <PlusCircleIcon className="h-5 w-5" />
            Create Room
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {/* Statistics Card */}
        <div className="bg-dark-lighter rounded-lg p-6 border border-gray-700">
          <div className="flex flex-col items-center text-center gap-4">
            <div className="bg-blue-800/30 p-4 rounded-full">
              <TrophyIcon className="h-12 w-12 text-blue-400" />
            </div>
            <h3 className="text-xl font-semibold text-white">Your Statistics</h3>
            <div className="grid grid-cols-2 gap-4 w-full">
              <div className="bg-dark-lightest p-3 rounded-lg">
                <p className="text-sm text-gray-400">Wins</p>
                <p className="text-2xl font-bold text-green-400">{stats.wins}</p>
              </div>
              <div className="bg-dark-lightest p-3 rounded-lg">
                <p className="text-sm text-gray-400">Losses</p>
                <p className="text-2xl font-bold text-red-400">{stats.losses}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Leaderboard Card */}
        <div className="bg-dark-lighter rounded-lg p-6 border border-gray-700">
          <div className="flex flex-col items-center text-center gap-4">
            <div className="bg-purple-800/30 p-4 rounded-full">
              <TrophyIcon className="h-12 w-12 text-purple-400" />
            </div>
            <h3 className="text-xl font-semibold text-white">Leaderboard</h3>
            {leaderboard.length > 0 ? (
              <div className="w-full">
                {leaderboard.slice(0, 5).map((player, index) => (
                  <div key={player.userId} className="flex justify-between items-center py-2 border-b border-gray-700 last:border-b-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-bold">{index + 1}.</span>
                      <span className="text-white">{player.username || player.userId.substring(0, 6) + '...'}</span>
                    </div>
                    <span className="text-blue-400 font-bold">{player.rating}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400">No leaderboard data available</p>
            )}
          </div>
        </div>

        {/* Active Players Card */}
        <div className="bg-dark-lighter rounded-lg p-6 border border-gray-700">
          <div className="flex flex-col items-center text-center gap-4">
            <div className="bg-green-800/30 p-4 rounded-full">
              <UserGroupIcon className="h-12 w-12 text-green-400" />
            </div>
            <h3 className="text-xl font-semibold text-white">Active Players</h3>
            <div className="text-3xl font-bold text-green-400">
              {activeRooms.reduce((count, room) => count + room.participants.length, 0)}
            </div>
            <p className="text-gray-300">across {activeRooms.length} active rooms</p>
          </div>
        </div>
      </div>

      {/* Active Rooms */}
      <h2 className="text-xl font-bold text-white mb-4">Active Rooms</h2>
      {isLoading ? (
        <div className="flex justify-center py-8">
          <ArrowPathIcon className="h-10 w-10 text-blue-500 animate-spin" />
        </div>
      ) : activeRooms.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {activeRooms.map(room => (
            <RoomCard
              key={room.roomId}
              room={room}
              currentUserId={user?.address}
              onJoin={handleJoinRoom}
            />
          ))}
        </div>
      ) : (
        <div className="bg-dark-lighter rounded-lg p-6 text-center">
          <p className="text-gray-400 mb-4">No active rooms available</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg"
          >
            <PlusCircleIcon className="h-5 w-5" />
            Create Room
          </button>
        </div>
      )}

      {/* Create Room Modal */}
      {showCreateModal && (
        <CreateRoomModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateRoom}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default Duel;
